import { Box, Button, Card, Paper, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getReferenceTables } from "../../../api/reference";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { ReferenceTable } from "../../../types/interfaces/admin";
import { toKebabCase } from "../../../utils";

const SchemaTablesPage: React.FC = () => {
  const { schema } = useParams<{ schema: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const [search, setSearch] = useState("");
  const [tables, setTables] = useState<ReferenceTable[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTables = async () => {
      if (!schema) return;
      setLoading(true);
      try {
        const allTables = await getReferenceTables();
        setTables(allTables.filter((t) => t.schemaName === schema));
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTables();
  }, [schema]);

  if (!schema) return null;

  const filteredTables = tables.filter((table) =>
    table.tableName.toLowerCase().includes(search.toLowerCase())
  );

  const renderTables = () => {
    if (loading) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("generic.loading")}
        </Typography>
      );
    }

    if (filteredTables.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("admin.reference.generic.no-tables-found")}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {filteredTables.map((table) => {
          const route = `${schema}/${toKebabCase(table.tableName)}`;
          return (
            <Card
              key={table.tableName}
              sx={{
                width: "calc(33.333% - 16px)",
                minWidth: 250,
                p: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
              variant="outlined"
            >
              <Typography variant="h6" gutterBottom>
                {table.tableName}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={() => navigate(`/admin/reference/${route}`)}
                >
                  {t("generic.view-details")}
                </Button>
              </Box>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper sx={{ width: "100%", p: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {t("admin.reference.generic.schema-tables-title", { schema })}
        </Typography>

        <TextField
          label={t("generic.search")}
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 3, maxWidth: 400 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {renderTables()}
      </Box>
    </Paper>
  );
};

export default SchemaTablesPage;
